import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {ViewerPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {OkCancelDialog} from '../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';
import {DetailedActivityModel} from '../../models/api/activityModel';
import {CommentModel} from '../../models/api/commentModel';
import {ViewerPanelPagesState} from '../../store/panels/viewer/viewerPanelState';
import {Frame} from '../common/frames/frame';
import {RequiredTaggingCoordinator} from '../inboxes/viewer/conversation/safetyNets/requiredTaggingCoordinator';
import {SafetyNetsProvider} from '../inboxes/viewer/conversation/safetyNets/safetyNetsContext';
import {PopupViewerComposer} from './popupViewerComposer';
import {PopupViewerConversationContainer} from './popupViewerConversationContainer';

/*
 * Constants.
 */

enum CloseConfirmModesEnum {
  NONE = 'none',
  POPOUT = 'popout',
  MAIN_WINDOW = 'main-window',
}

/*
 * Props.
 */

interface PopupViewerProps {
  panelId: ViewerPanelId;
  shouldForcePopout?: boolean;
  pages: ViewerPanelPagesState;
  composeComment?: CommentModel;
  originalLinkedConversationActivity: DetailedActivityModel | null;
  isComposing: boolean;
  shouldConfirmClose: boolean;
  onRequestClose: () => void;
  onPopoutClick: () => void;
  onConfirmDeleteClick: (activity: DetailedActivityModel) => void;
}

/*
 * Style.
 */

const StyledViewerDiv = styled.div<{
  $hasVerticalLayout: boolean;
}>`
  flex: 1;
  min-height: 0;
  ${stretch()};
  ${(p) =>
    p.$hasVerticalLayout &&
    css`
      flex-direction: column;
    `}
`;

/*
 * Component.
 */

export const PopupViewer: FC<PopupViewerProps> = (props) => {
  const [confirmCloseMode, setConfirmCloseMode] = useState(CloseConfirmModesEnum.NONE);

  const onRequestClose = (forceClose?: boolean) => {
    if (!props.shouldConfirmClose) {
      props.onRequestClose();
      return;
    }

    const mode = forceClose ? CloseConfirmModesEnum.MAIN_WINDOW : CloseConfirmModesEnum.POPOUT;
    setConfirmCloseMode(mode);
  };

  const onConfirmCloseClose = () => {
    setConfirmCloseMode(CloseConfirmModesEnum.NONE);
  };

  const renderDeleteConfirmation = () => {
    const onOkClick = () => {
      if (props.originalLinkedConversationActivity) {
        props.onConfirmDeleteClick(props.originalLinkedConversationActivity);
      }

      props.onRequestClose();
    };

    return (
      <OkCancelDialog
        title={<Trans id="composer-internal-delete-title">Delete draft</Trans>}
        okButtonLabel={<Trans id="composer-internal-delete-ok">Delete</Trans>}
        onOkClick={onOkClick}
        onRequestClose={onConfirmCloseClose}
      >
        <Trans id="composer-internal-delete-content">
          Would you like to delete the current draft?
          <br />
          This action cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  };

  const maybeRenderConfirmCloseInFrame = () => {
    if (confirmCloseMode === CloseConfirmModesEnum.POPOUT) {
      return renderDeleteConfirmation();
    }
    return null;
  };

  if (confirmCloseMode === CloseConfirmModesEnum.MAIN_WINDOW) {
    return renderDeleteConfirmation();
  }

  return (
    <ExpandedDarkThemeProvider>
      <SafetyNetsProvider>
        <Frame
          minWidth="540px"
          width={props.composeComment ? 620 : 740}
          shouldForcePopout={props.shouldForcePopout}
          onRequestClose={onRequestClose}
          startHeight={props.isComposing ? 450 : undefined}
          onOpenPopout={props.onPopoutClick}
        >
          <RequiredTaggingCoordinator />
          <StyledViewerDiv $hasVerticalLayout={!(props.isComposing && props.composeComment)}>
            {renderContent({...props, onRequestClose})}
          </StyledViewerDiv>
          {maybeRenderConfirmCloseInFrame()}
        </Frame>
      </SafetyNetsProvider>
    </ExpandedDarkThemeProvider>
  );
};

function renderContent(props: PopupViewerProps) {
  if (props.isComposing && props.composeComment) {
    return <PopupViewerComposer {...props} commentUid={props.composeComment.uid} />;
  }

  const shouldFocusComment = Boolean(props.composeComment);
  return <PopupViewerConversationContainer {...props} shouldFocusComment={shouldFocusComment} />;
}
